/** Interactive plot: hover readout, drag-to-zoom (x), wheel zoom, double-click reset. */
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { fmt } from '../core/units';
import { renderCanvas, renderSvg } from './render';
import { buildScene, type PlotSpec, type Scene } from './scene';

export interface PlotHandle {
  /** Render the plot at the current zoom to PNG at `scale` times the on-screen size. */
  png(scale: number, width?: number, height?: number): Promise<Blob | null>;
  svg(width?: number, height?: number): string;
}

interface Props {
  spec: PlotSpec;
  /** Changing this key resets the zoom. */
  resetKey?: string;
  onHover?: (x: number | null) => void;
  /** Externally driven hover position (e.g. from the image). */
  hoverX?: number | null;
  handle?: { current: PlotHandle | null };
  testId?: string;
  emptyText?: string;
}

function nearestIndex(x: ArrayLike<number>, v: number): number {
  let lo = 0;
  let hi = x.length - 1;
  if (hi < 0) return -1;
  if (x[hi] < x[0]) {
    // descending: linear scan
    let b = 0;
    for (let i = 1; i < x.length; i++) if (Math.abs(x[i] - v) < Math.abs(x[b] - v)) b = i;
    return b;
  }
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1;
    if (x[m] < v) lo = m;
    else hi = m;
  }
  return Math.abs(x[lo] - v) <= Math.abs(x[hi] - v) ? lo : hi;
}

export function PlotView({ spec, resetKey, onHover, hoverX, handle, testId, emptyText }: Props) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 300, h: 200 });
  const [xRange, setXRange] = useState<[number, number] | null>(null);
  const [hover, setHover] = useState<{ px: number; py: number } | null>(null);
  const [sel, setSel] = useState<{ a: number; b: number } | null>(null);
  const scene = useRef<Scene | null>(null);

  useEffect(() => setXRange(null), [resetKey]);

  // Measure before the first paint so the canvas never shows a frame rendered at a stale size.
  useLayoutEffect(() => {
    const measure = () => {
      const el = wrap.current;
      if (el)
        setSize((s) => {
          const w = Math.max(50, el.clientWidth);
          const h = Math.max(50, el.clientHeight);
          return s.w === w && s.h === h ? s : { w, h };
        });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap.current!);
    return () => ro.disconnect();
  }, []);

  const empty = !spec.series.some((s) => s.y.length > 0);
  const full = { ...spec, xRange: xRange ?? spec.xRange ?? null };

  useEffect(() => {
    const c = canvas.current!;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(size.w * dpr);
    c.height = Math.round(size.h * dpr);
    const s = buildScene(full, size.w, size.h);
    scene.current = s;
    const ctx = c.getContext('2d')!;
    renderCanvas(ctx, s, dpr);
    // Hover / external cursor and selection overlay.
    const hx = hover ? s.invX(hover.px) : hoverX;
    if (hx !== null && hx !== undefined && Number.isFinite(hx) && !empty) {
      const x = s.sx(hx);
      if (x >= s.plot.x && x <= s.plot.x + s.plot.w) {
        ctx.strokeStyle = 'rgba(255,255,255,0.55)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(x, s.plot.y);
        ctx.lineTo(x, s.plot.y + s.plot.h);
        ctx.stroke();
        ctx.setLineDash([]);
        for (const ser of spec.series) {
          const i = nearestIndex(ser.x, hx);
          if (i < 0 || !Number.isFinite(ser.y[i]) || ser.axis === 'y2') continue;
          ctx.beginPath();
          ctx.arc(s.sx(ser.x[i]), s.sy(ser.y[i]), 3.5, 0, Math.PI * 2);
          ctx.fillStyle = ser.color;
          ctx.fill();
          ctx.strokeStyle = '#000';
          ctx.stroke();
        }
      }
    }
    if (sel) {
      ctx.fillStyle = 'rgba(120,170,255,0.18)';
      ctx.fillRect(Math.min(sel.a, sel.b), s.plot.y, Math.abs(sel.b - sel.a), s.plot.h);
    }
  });

  if (handle)
    handle.current = {
      png: (k, w, h) =>
        new Promise((res) => {
          const W = w ?? size.w;
          const H = h ?? size.h;
          const c = document.createElement('canvas');
          c.width = Math.round(W * k);
          c.height = Math.round(H * k);
          renderCanvas(c.getContext('2d')!, buildScene(full, W, H), k);
          c.toBlob(res, 'image/png');
        }),
      svg: (w, h) => renderSvg(buildScene(full, w ?? size.w, h ?? size.h)),
    };

  const local = (e: MouseEvent) => {
    const r = canvas.current!.getBoundingClientRect();
    return { px: e.clientX - r.left, py: e.clientY - r.top };
  };

  const onMove = (e: PointerEvent) => {
    const p = local(e);
    setHover(p);
    const s = scene.current;
    if (sel) setSel({ ...sel, b: Math.max(s!.plot.x, Math.min(s!.plot.x + s!.plot.w, p.px)) });
    if (s && onHover) {
      const inside = p.px >= s.plot.x && p.px <= s.plot.x + s.plot.w;
      onHover(inside ? s.invX(p.px) : null);
    }
  };

  const onDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const p = local(e);
    const s = scene.current;
    if (!s || p.px < s.plot.x || p.px > s.plot.x + s.plot.w) return;
    canvas.current!.setPointerCapture(e.pointerId);
    setSel({ a: p.px, b: p.px });
  };

  const onUp = () => {
    const s = scene.current;
    if (sel && s && Math.abs(sel.b - sel.a) > 4) {
      const a = s.invX(Math.min(sel.a, sel.b));
      const b = s.invX(Math.max(sel.a, sel.b));
      setXRange([a, b]);
    }
    setSel(null);
  };

  const onWheel = (e: WheelEvent) => {
    const s = scene.current;
    if (!s) return;
    e.preventDefault();
    const p = local(e);
    const c = s.invX(p.px);
    const k = Math.exp(e.deltaY * 0.0015);
    const [a, b] = s.xRange;
    setXRange([c - (c - a) * k, c + (b - c) * k]);
  };

  useEffect(() => {
    const c = canvas.current!;
    c.addEventListener('wheel', onWheel, { passive: false });
    return () => c.removeEventListener('wheel', onWheel);
  });

  // Readout of values at the hovered x.
  let readout: { label: string; color: string; value: string }[] = [];
  let hxText = '';
  const s = scene.current;
  if (hover && s && !empty && hover.px >= s.plot.x && hover.px <= s.plot.x + s.plot.w) {
    const hx = s.invX(hover.px);
    hxText = fmt(hx, 5);
    readout = spec.series.map((ser) => {
      const i = nearestIndex(ser.x, hx);
      return { label: ser.label, color: ser.color, value: i >= 0 ? fmt(ser.y[i], 6) : '—' };
    });
  }

  return (
    <div class="plot" ref={wrap} data-testid={testId}>
      <canvas
        ref={canvas}
        style={{ width: `${size.w}px`, height: `${size.h}px` }}
        onPointerMove={onMove}
        onPointerDown={onDown}
        onPointerUp={onUp}
        onPointerLeave={() => {
          setHover(null);
          onHover?.(null);
        }}
        onDblClick={() => setXRange(null)}
      />
      {empty && <div class="plot-empty">{emptyText ?? 'No data'}</div>}
      {readout.length > 0 && hover && (
        <div class="plot-readout" style={{ left: `${Math.min(hover.px + 14, size.w - 190)}px`, top: '8px' }}>
          <div class="muted">
            {spec.xLabel.split('[')[0].trim()} = {hxText}
          </div>
          {readout.map((r) => (
            <div key={r.label}>
              <span class="sw" style={{ background: r.color }} />
              {r.label}: <b>{r.value}</b>
            </div>
          ))}
        </div>
      )}
      {xRange && (
        <button class="plot-reset" onClick={() => setXRange(null)} data-help="plot.reset">
          Reset zoom
        </button>
      )}
    </div>
  );
}
